import { createSelector } from '@reduxjs/toolkit';
import { BlockComponent } from 'types';
import type { StorytellerState } from 'store/StorytellerReduxState';
import {
  storyReducerStoriesSelector,
  storyReducerBlocksSelector,
  storyReducerSelector,
} from 'store/TopLevelSelector';

import {
  doesStoryExist,
  isStoryInternal,
  isStoryPublic,
  getStoryPublishedStory,
  isDraftUnpublished,
  draftHasChanges,
  getBlockAndComponent,
  getStory,
  getStoryTitle,
  getStoryTitleToCopy,
  getStoryDescription,
  getStoryMetadata,
  getStoryPrivateMetadata,
  getStoryTileTitle,
  getStoryTileDescription,
  getStoryTheme,
  getStoryLayout,
  getStoryUpdatedAt,
  getStoryPermissions,
} from 'store/selectors/StorySelectors/Stories';
import { selectSerializedDerivedReport, selectSerializedStory } from './StorySerialize';
import { isStorySavePossible, isStorySaved } from '../StorySaveStatusSelectors';

import {
  getStoryBlockIds,
  getStoryBlockAtIndex,
  getStoryBlockIdAtIndex,
  getBlockLayout,
  getBlockComponents,
  isBlockPresentable,
  getBlockBackgroundColor,
  getBlockComponentAtIndex,
  isBlockRestrictedToMeasureCardsOnly,
  getStoryBlocks,
  getGlobalFilterBar,
} from 'store/selectors/StorySelectors/Blocks';
import { selectStoryDigest } from 'store/reducers/DigestReducer';

export const selectors = {
  doesStoryExist: (state: StorytellerState, storyUid: string) =>
    doesStoryExist(storyUid, storyReducerStoriesSelector(state)),
  isStoryPublic: (state: StorytellerState, storyUid: string) =>
    isStoryPublic(storyUid, storyReducerStoriesSelector(state)),
  isStoryInternal: (state: StorytellerState, storyUid: string) =>
    isStoryInternal(storyUid, storyReducerStoriesSelector(state)),
  isStorySaved,
  isStorySavePossible,
  getStoryPublishedStory: (state: StorytellerState, storyUid: string) =>
    getStoryPublishedStory(storyUid, storyReducerStoriesSelector(state)),
  isDraftUnpublished: (state: StorytellerState, storyUid: string) =>
    isDraftUnpublished(storyUid, storyReducerStoriesSelector(state), selectStoryDigest(state)),
  draftHasChanges: (state: StorytellerState, storyUid: string) =>
    draftHasChanges(storyUid, storyReducerStoriesSelector(state), selectStoryDigest(state)),
  getBlockAndComponent: (state: StorytellerState, blockId: string, componentIndex: number) =>
    getBlockAndComponent(blockId, componentIndex, storyReducerBlocksSelector(state)),
  getStoryTitle: (state: StorytellerState, storyUid: string) =>
    getStoryTitle(storyUid, storyReducerStoriesSelector(state)),
  getStoryTitleToCopy: (state: StorytellerState) => getStoryTitleToCopy(storyReducerStoriesSelector(state)),
  getStory: (state: StorytellerState, storyUid: string) =>
    getStory(storyUid, storyReducerStoriesSelector(state)),
  getStoryDescription: (state: StorytellerState, storyUid: string) =>
    getStoryDescription(storyUid, storyReducerStoriesSelector(state)),
  getStoryMetadata: (state: StorytellerState, storyUid: string) =>
    getStoryMetadata(storyUid, storyReducerStoriesSelector(state)),
  getStoryPrivateMetadata: (state: StorytellerState, storyUid: string) =>
    getStoryPrivateMetadata(storyUid, storyReducerStoriesSelector(state)),
  getStoryTileTitle: (state: StorytellerState, storyUid: string) =>
    getStoryTileTitle(storyUid, storyReducerStoriesSelector(state)),
  getStoryTileDescription: (state: StorytellerState, storyUid: string) =>
    getStoryTileDescription(storyUid, storyReducerStoriesSelector(state)),
  getStoryTheme: (state: StorytellerState, storyUid: string) =>
    getStoryTheme(storyUid, storyReducerStoriesSelector(state)),
  getStoryLayout: (state: StorytellerState, storyUid: string) =>
    getStoryLayout(storyUid, storyReducerStoriesSelector(state)),
  getStoryUpdatedAt: (state: StorytellerState, storyUid: string) =>
    getStoryUpdatedAt(storyUid, storyReducerStoriesSelector(state)),
  getStoryPermissions: (state: StorytellerState, storyUid: string) =>
    getStoryPermissions(storyUid, storyReducerStoriesSelector(state)),
  getBlocks: storyReducerBlocksSelector,
  getStoryBlockIds: (state: StorytellerState, storyUid: string) =>
    getStoryBlockIds(storyUid, storyReducerStoriesSelector(state)),
  getStoryBlocks: (storyUid: string) => (state: StorytellerState) =>
    getStoryBlocks(storyReducerSelector(state), storyUid),
  getGlobalFilterBar: (storyUid: string) => (state: StorytellerState) =>
    getGlobalFilterBar(getStoryBlocks(storyReducerSelector(state), storyUid)),
  getStoryBlockAtIndex: (state: StorytellerState, storyUid: string, index: number) =>
    getStoryBlockAtIndex(
      storyUid,
      index,
      storyReducerStoriesSelector(state),
      storyReducerBlocksSelector(state),
    ),
  getStoryBlockIdAtIndex: (state: StorytellerState, storyUid: string, index: number) =>
    getStoryBlockIdAtIndex(storyUid, index, storyReducerStoriesSelector(state)),
  getBlockLayout: (state: StorytellerState, blockId: string) =>
    getBlockLayout(blockId, storyReducerBlocksSelector(state)),
  getBlockComponents: (state: StorytellerState, blockId: string): BlockComponent[] =>
    getBlockComponents(blockId, storyReducerBlocksSelector(state)),
  isBlockPresentable: (state: StorytellerState, blockId: string) =>
    isBlockPresentable(blockId, storyReducerBlocksSelector(state)),
  getBlockBackgroundColor: (state: StorytellerState, blockId: string) =>
    getBlockBackgroundColor(blockId, storyReducerBlocksSelector(state)),
  getBlockComponentAtIndex: (state: StorytellerState, blockId: string, index: number): BlockComponent =>
    getBlockComponentAtIndex(blockId, index, storyReducerBlocksSelector(state)),

  serializeStory: selectSerializedStory,
  serializeDerivedStory: selectSerializedDerivedReport,

  isBlockRestrictedToMeasureCardsOnly: (state: StorytellerState, blockId: string) =>
    isBlockRestrictedToMeasureCardsOnly(blockId, storyReducerBlocksSelector(state)),

  selectBlocksThatNeedConfirmation: createSelector(storyReducerSelector, (storyState) => {
    return storyState.blocksThatNeedConfirmation;
  }),
};
